import React, { useState, useEffect, useContext, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Collapse, CircularProgress, Tooltip } from '@material-ui/core';
import {
  ChevronRight as ChevronRightIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Check as CheckIcon,
  Close as CloseIcon,
} from '@material-ui/icons';
import clsx from 'clsx';

import { EMAIL_TEMPLATE_CATEGORY_SERVICES } from 'Services';
import { ToggleTickSwitch } from 'components';
import { Context } from 'context';
import { ADD_NOTIFICATION } from 'context/actions';
import { formatDate } from 'utils';
import DropdownPill from './DropdownPill';
import styles from './CategorizedCommunicationTemplates.module.scss';

const STATUS_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

const CategorizedCommunicationTemplates = ({ level, group_id, location_id, isSync = false, title = 'Email Templates' }) => {
  const levelId = level === 'location' ? location_id : group_id;

  const svc = {
    fetch: isSync
      ? EMAIL_TEMPLATE_CATEGORY_SERVICES.getSyncCategorizedTemplates
      : EMAIL_TEMPLATE_CATEGORY_SERVICES.getCategorizedTemplates,
    update: isSync
      ? EMAIL_TEMPLATE_CATEGORY_SERVICES.updateSyncTemplate
      : EMAIL_TEMPLATE_CATEGORY_SERVICES.updateTemplate,
    toggle: isSync
      ? EMAIL_TEMPLATE_CATEGORY_SERVICES.toggleSyncCategoryStatus
      : EMAIL_TEMPLATE_CATEGORY_SERVICES.toggleCategoryStatus,
    toggleNoCategory: isSync
      ? EMAIL_TEMPLATE_CATEGORY_SERVICES.toggleSyncNoCategoryStatus
      : EMAIL_TEMPLATE_CATEGORY_SERVICES.toggleNoCategoryStatus,
  };
  const [data, setData] = useState({ categories: [], no_category: [], total_templates: 0 });
  const [isLoading, setIsLoading] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [savingCell, setSavingCell] = useState(null);
  const [togglingCategory, setTogglingCategory] = useState(null);
  const [togglingTemplate, setTogglingTemplate] = useState(null);
  const [togglingNoCategory, setTogglingNoCategory] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const { dispatch } = useContext(Context);

  const notify = useCallback(
    (severity, message) =>
      dispatch({ type: ADD_NOTIFICATION, payload: { notification: { severity, message } } }),
    [dispatch]
  );

  const fetchData = useCallback(async () => {
    if (!level || !levelId) return;

    setIsLoading(true);
    try {
      const response = await svc.fetch(level, levelId);
      if (response?.type === 'success') {
        setData({
          categories: response.data?.categories || [],
          no_category: response.data?.no_category || [],
          total_templates: response.data?.total_templates || 0,
        });
      } else {
        throw response;
      }
    } catch (err) {
      notify('error', err?.message || err?.error || 'Failed to load email templates');
    }
    setIsLoading(false);
  }, [level, levelId, notify]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const toggleExpand = (categoryId) =>
    setExpandedCategories((prev) => ({ ...prev, [categoryId]: !prev[categoryId] }));

  // ── Category toggle (bulk enable/disable all templates in a category) ──
  const handleCategoryToggle = async (categoryId, newStatus, e) => {
    e.stopPropagation();
    setTogglingCategory(categoryId);
    try {
      const response = await svc.toggle(categoryId, {
        level,
        level_id: levelId,
        enabled: newStatus,
      });
      if (response?.type === 'success') {
        notify('success', `Category templates ${newStatus ? 'enabled' : 'disabled'}`);
        await fetchData();
      } else {
        throw response;
      }
    } catch (err) {
      notify('error', err?.message || err?.error || 'Failed to update category status');
    }
    setTogglingCategory(null);
  };

  // ── No-category bulk toggle ──
  const handleNoCategoryToggle = async (newStatus, e) => {
    e.stopPropagation();
    const eventNames = (data.no_category || []).map((t) => t.event);
    if (!eventNames.length) return;
    setTogglingNoCategory(true);
    try {
      const response = await svc.toggleNoCategory({
        level,
        level_id: levelId,
        enabled: newStatus,
        template_ids: eventNames,
      });
      if (response?.type === 'success') {
        notify('success', `Uncategorized templates ${newStatus ? 'enabled' : 'disabled'}`);
        await fetchData();
      } else {
        throw response;
      }
    } catch (err) {
      notify('error', err?.message || err?.error || 'Failed to update uncategorized template status');
    }
    setTogglingNoCategory(false);
  };

  // ── Individual template toggle ──
  // Backend requires event + template_id + enabled (all three, extra="forbid")
  const handleTemplateToggle = async (categoryId, event, templateId, newStatus) => {
    setTogglingTemplate(`${categoryId}-${event}`);
    try {
      const response = await svc.update(level, levelId, {
        email_templates: [{ event, template_id: templateId || '', enabled: newStatus }],
      });
      if (response?.type === 'success') {
        notify('success', 'Template status updated');
        await fetchData();
      } else {
        throw response;
      }
    } catch (err) {
      notify('error', err?.message || err?.error || 'Failed to update template status');
    }
    setTogglingTemplate(null);
  };

  // ── Sendgrid ID inline edit ──
  const startEdit = (categoryId, event, currentTemplateId, currentEnabled) => {
    setEditingCell({ categoryId, event, currentEnabled });
    setEditValue(currentTemplateId || '');
  };

  const cancelEdit = () => {
    setEditingCell(null);
    setEditValue('');
  };

  const saveEdit = async () => {
    if (!editingCell) return;
    const { event, currentEnabled } = editingCell;
    setSavingCell(event);
    try {
      // Backend requires event + template_id + enabled together (extra="forbid")
      const response = await svc.update(level, levelId, {
        email_templates: [{ event, template_id: editValue, enabled: Boolean(currentEnabled) }],
      });
      if (response?.type === 'success') {
        notify('success', 'Sendgrid ID updated');
        setEditingCell(null);
        setEditValue('');
        await fetchData();
      } else {
        throw response;
      }
    } catch (err) {
      notify('error', err?.message || err?.error || 'Failed to update Sendgrid ID');
    }
    setSavingCell(null);
  };

  const isEditingCell = (categoryId, event) =>
    editingCell?.categoryId === categoryId && editingCell?.event === event;

  // ── Effective enabled state (factors in group_enabled for location level) ──
  const isEffectivelyEnabled = (template) => {
    if (level === 'location' && template.group_enabled === false) return false;
    return Boolean(template.enabled);
  };

  // ── Category metadata helpers ──
  const getCategoryMeta = (category) => {
    const templates = category.templates || [];
    const count = templates.length;
    const someEnabled = templates.some((t) => isEffectivelyEnabled(t));
    const allDisabledByGroup = level === 'location' && count > 0 && templates.every((t) => t.group_enabled === false);
    return {
      count,
      someEnabled,
      allDisabledByGroup,
      updatedBy: category.updated_by || '-',
      updatedAt: category.updated_at ? formatDate(category.updated_at) : '-',
    };
  };

  // ── Filtering ──
  const matchesSearch = (template) => {
    if (!searchTerm.trim()) return true;
    const term = searchTerm.toLowerCase();
    return (
      template.event?.toLowerCase().includes(term) ||
      template.template_id?.toLowerCase().includes(term)
    );
  };

  const matchesStatus = (template) => {
    if (!statusFilter) return true;
    return statusFilter === 'active' ? isEffectivelyEnabled(template) : !isEffectivelyEnabled(template);
  };

  const getFilteredTemplates = (templates = []) =>
    templates.filter((t) => matchesSearch(t) && matchesStatus(t));

  // API response uses `id` and `name` — normalise for display
  const normaliseCategory = (cat) => ({
    ...cat,
    category_id: cat.id,
    category_name: cat.name,
  });

  const sortedCategories = [...(data.categories || [])]
    .map(normaliseCategory)
    .sort((a, b) => (a.display_order || 0) - (b.display_order || 0));

  const categoryOptions = [
    { value: '', label: 'All' },
    ...sortedCategories.map((c) => ({ value: c.category_id, label: c.category_name })),
    ...(data.no_category?.length > 0 ? [{ value: '__uncategorized', label: 'Uncategorized' }] : []),
  ];

  const visibleCategories = sortedCategories.filter((category) => {
    if (categoryFilter === '__uncategorized') return false;
    if (categoryFilter && category.category_id !== categoryFilter) return false;

    const templates = category.templates || [];
    if (statusFilter === 'active' && !templates.some((t) => isEffectivelyEnabled(t))) return false;
    if (statusFilter === 'inactive' && templates.every((t) => isEffectivelyEnabled(t))) return false;
    if (searchTerm.trim() && !templates.some(matchesSearch)) return false;

    return true;
  });

  const showUncategorized =
    (!categoryFilter || categoryFilter === '__uncategorized') &&
    (data.no_category || []).some((t) => matchesSearch(t) && matchesStatus(t));

  const filteredUncategorized = (data.no_category || []).filter(
    (t) => matchesSearch(t) && matchesStatus(t)
  );

  const noCategoryTemplates = data.no_category || [];
  const allNoCategoryDisabledByGroup = level === 'location' && noCategoryTemplates.length > 0 && noCategoryTemplates.every((t) => t.group_enabled === false);
  const noCategoryEnabled = noCategoryTemplates.some((t) => isEffectivelyEnabled(t));

  const isEmpty =
    !isLoading &&
    visibleCategories.length === 0 &&
    (!showUncategorized || filteredUncategorized.length === 0);

  // ── Template row ──
  const renderTemplateRow = (template, categoryId) => {
    const { event, template_id, updated_by, updated_at, enabled, group_enabled } = template;
    const editing = isEditingCell(categoryId, event);
    const isSaving = savingCell === event;
    const isDisabledByGroup = level === 'location' && !group_enabled;
    const isChecked = isDisabledByGroup ? false : Boolean(enabled);
    const isTogglingThis = togglingTemplate === `${categoryId}-${event}`;

    return (
      <div className={styles.templateRow} key={`${categoryId}-${event}`}>
        <div className={styles.colSpacer16} />
        <div className={styles.colArrow} />

        <div className={clsx(styles.colUpdatedBy, styles.cell)}>
          {updated_by || '-'}
        </div>

        <div className={clsx(styles.colUpdatedAt, styles.cell)}>
          {updated_at ? formatDate(updated_at) : '-'}
        </div>

        <div className={clsx(styles.colTriggerEvents, styles.cell)}>
          <span className={styles.triggerText}>{event}</span>
        </div>

        <div className={clsx(styles.colSendgrid, styles.cell)}>
          {editing ? (
            <div className={styles.sendgridEditRow}>
              <input
                className={styles.sendgridInput}
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && saveEdit()}
                autoFocus
                disabled={isSaving}
              />
              <div className={styles.actionButtons}>
                {isSaving ? (
                  <CircularProgress size={16} style={{ color: '#FF7B9F' }} />
                ) : (
                  <>
                    <button className={styles.saveBtn} onClick={saveEdit} type="button" aria-label="Save">
                      <CheckIcon aria-hidden="true" />
                    </button>
                    <button className={styles.cancelBtn} onClick={cancelEdit} type="button" aria-label="Cancel">
                      <CloseIcon aria-hidden="true" />
                    </button>
                  </>
                )}
              </div>
            </div>
          ) : (
            <div className={styles.sendgridContent}>
              <span className={styles.sendgridText}>{template_id || '-'}</span>
              <button
                type="button"
                className={styles.editBtn}
                onClick={() => startEdit(categoryId, event, template_id, enabled)}
              >
                <EditIcon />
              </button>
            </div>
          )}
        </div>

        <div className={clsx(styles.colStatus, styles.cell)}>
          <Tooltip
            title={isDisabledByGroup ? 'This email template is inactive at group level' : ''}
            placement="bottom"
            arrow
            disableHoverListener={!isDisabledByGroup}
          >
            <div className={styles.statusContent}>
              {isTogglingThis ? (
                <CircularProgress size={16} style={{ color: '#FF7B9F' }} />
              ) : (
                <div className={styles.dsToggleWrap}>
                  <ToggleTickSwitch
                    checked={isChecked}
                    onChange={(e) => handleTemplateToggle(categoryId, event, template_id, e.target.checked)}
                    disabled={isDisabledByGroup}
                  />
                </div>
              )}
              <span className={styles.statusLabel}>{isChecked ? 'Active' : 'Inactive'}</span>
            </div>
          </Tooltip>
        </div>

        <div className={styles.colSpacer16} />
      </div>
    );
  };

  // ── Category active/inactive counts ──
  const getCategoryCounts = (category) => {
    const templates = category.templates || [];
    const activeCount = templates.filter((t) => isEffectivelyEnabled(t)).length;
    const inactiveCount = templates.length - activeCount;
    return { activeCount, inactiveCount };
  };

  // ── Category row + its children ──
  const renderCategoryBlock = (category) => {
    const { category_id, category_name, templates = [] } = category;
    const expanded = Boolean(expandedCategories[category_id]);
    const meta = getCategoryMeta(category);
    const { activeCount, inactiveCount } = getCategoryCounts(category);
    const filteredTemplates = getFilteredTemplates(templates);
    const isTogglingThis = togglingCategory === category_id;

    return (
      <React.Fragment key={category_id}>
        <button
          type="button"
          className={clsx(styles.row, styles.rowClickable)}
          onClick={() => toggleExpand(category_id)}
        >
          <div className={styles.colSpacer16} />

          <div className={styles.colArrow}>
            <div className={styles.expandBtn}>
              <ChevronRightIcon
                className={clsx(styles.expandIcon, { [styles.expandIconOpen]: expanded })}
              />
            </div>
          </div>

          <div className={clsx(styles.colCategoryName, styles.cell)}>
            <div className={styles.categoryContent}>
              <span className={styles.categoryName}>{category_name}</span>
              <span className={styles.badge}>{meta.count}</span>
            </div>
          </div>

          <div className={clsx(styles.colCategoryCounts, styles.cell)}>
            <span className={styles.activeCount}>{activeCount} Active</span>
            <span className={styles.countDivider} />
            <span className={styles.inactiveCount}>{inactiveCount} Inactive</span>
          </div>

          <div className={clsx(styles.colStatus, styles.cell)}>
            <div className={styles.statusContent}>
              {isTogglingThis ? (
                <CircularProgress size={16} style={{ color: '#FF7B9F' }} />
              ) : (
                <Tooltip
                  title={meta.allDisabledByGroup ? 'All templates in this category are inactive at group level' : ''}
                  placement="bottom"
                  arrow
                  disableHoverListener={!meta.allDisabledByGroup}
                >
                  <div className={styles.dsToggleWrap}>
                    <ToggleTickSwitch
                      checked={meta.someEnabled}
                      onChange={(e) => { e.stopPropagation(); handleCategoryToggle(category_id, e.target.checked, e); }}
                      disabled={meta.allDisabledByGroup}
                    />
                  </div>
                </Tooltip>
              )}
              <span className={styles.statusLabel}>
                {meta.someEnabled ? 'Active' : 'Inactive'}
              </span>
            </div>
          </div>

          <div className={styles.colSpacer16} />
        </button>

        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <div className={styles.collapseInner}>
            {/* Sub-header row */}
            <div className={styles.subHeader}>
              <div className={styles.colSpacer16} />
              <div className={styles.colArrow} />
              <div className={clsx(styles.colUpdatedBy, styles.subHeaderCell)}>Updated by</div>
              <div className={clsx(styles.colUpdatedAt, styles.subHeaderCell)}>Updated at</div>
              <div className={clsx(styles.colTriggerEvents, styles.subHeaderCell)}>Triggering Events</div>
              <div className={clsx(styles.colSendgrid, styles.subHeaderCell)}>Sendgrid ID</div>
              <div className={clsx(styles.colStatus, styles.subHeaderCell)} />
              <div className={styles.colSpacer16} />
            </div>

            {filteredTemplates.length > 0
              ? filteredTemplates.map((t) => renderTemplateRow(t, category_id))
              : (
                <div className={styles.templateRow}>
                  <div className={styles.emptyMessage}>No templates match the current filters</div>
                </div>
              )
            }
          </div>
        </Collapse>
      </React.Fragment>
    );
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.pageTitle}>{title}</div>

      <div className={styles.card}>
        {/* Filter bar */}
        <div className={styles.filterBar}>
          <div className={styles.filterSlot}>
            <div className={styles.searchPill}>
              <SearchIcon className={styles.searchIcon} />
              <input
                className={styles.searchInput}
                placeholder="Search event"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <DropdownPill
              label="Category"
              value={categoryFilter}
              options={categoryOptions}
              onChange={setCategoryFilter}
            />

            <DropdownPill
              label="Status"
              value={statusFilter}
              options={STATUS_OPTIONS}
              onChange={setStatusFilter}
            />
          </div>
        </div>

        {/* Scrollable table area */}
        <div className={styles.tableScrollArea}>
          <div className={styles.tableInner}>
            {/* Table header */}
            <div className={styles.tableHeader}>
              <div className={styles.colSpacer16} />
              <div className={styles.colArrow} />
              <div className={clsx(styles.colCategoryFull, styles.headerCell)}>Category</div>
              <div className={clsx(styles.colStatus, styles.headerCell)}>Status</div>
              <div className={styles.colSpacer16} />
            </div>

            {/* Loading state */}
            {isLoading && (
              <div className={styles.row}>
                <div className={styles.loadingMessage}>
                  <CircularProgress size={20} style={{ color: '#FF7B9F' }} />
                </div>
              </div>
            )}

            {/* Category blocks */}
            {!isLoading && visibleCategories.map(renderCategoryBlock)}

            {/* Uncategorized templates */}
            {!isLoading && showUncategorized && filteredUncategorized.length > 0 && (
              <React.Fragment>
                <button
                  type="button"
                  className={clsx(styles.row, styles.rowClickable)}
                  onClick={() => toggleExpand('__uncategorized')}
                >
                  <div className={styles.colSpacer16} />
                  <div className={styles.colArrow}>
                    <div className={styles.expandBtn}>
                      <ChevronRightIcon
                        className={clsx(styles.expandIcon, {
                          [styles.expandIconOpen]: expandedCategories['__uncategorized'],
                        })}
                      />
                    </div>
                  </div>
                  <div className={clsx(styles.colCategoryName, styles.cell)}>
                    <div className={styles.categoryContent}>
                      <span className={styles.categoryName}>Uncategorized</span>
                      <span className={styles.badge}>{filteredUncategorized.length}</span>
                    </div>
                  </div>
                  <div className={clsx(styles.colCategoryCounts, styles.cell)}>
                    <span className={styles.activeCount}>
                      {filteredUncategorized.filter((t) => isEffectivelyEnabled(t)).length} Active
                    </span>
                    <span className={styles.countDivider} />
                    <span className={styles.inactiveCount}>
                      {filteredUncategorized.filter((t) => !isEffectivelyEnabled(t)).length} Inactive
                    </span>
                  </div>
                  <div className={clsx(styles.colStatus, styles.cell)}>
                    <div className={styles.statusContent}>
                      {togglingNoCategory ? (
                        <CircularProgress size={16} style={{ color: '#FF7B9F' }} />
                      ) : (
                        <Tooltip
                          title={allNoCategoryDisabledByGroup ? 'All templates in this category are inactive at group level' : ''}
                          placement="bottom"
                          arrow
                          disableHoverListener={!allNoCategoryDisabledByGroup}
                        >
                          <div className={styles.dsToggleWrap}>
                            <ToggleTickSwitch
                              checked={noCategoryEnabled}
                              onChange={(e) => { e.stopPropagation(); handleNoCategoryToggle(e.target.checked, e); }}
                              disabled={allNoCategoryDisabledByGroup}
                            />
                          </div>
                        </Tooltip>
                      )}
                      <span className={styles.statusLabel}>
                        {noCategoryEnabled ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                  <div className={styles.colSpacer16} />
                </button>

                <Collapse
                  in={Boolean(expandedCategories['__uncategorized'])}
                  timeout="auto"
                  unmountOnExit
                >
                  <div className={styles.collapseInner}>
                    {/* Sub-header row */}
                    <div className={styles.subHeader}>
                      <div className={styles.colSpacer16} />
                      <div className={styles.colArrow} />
                      <div className={clsx(styles.colUpdatedBy, styles.subHeaderCell)}>Updated by</div>
                      <div className={clsx(styles.colUpdatedAt, styles.subHeaderCell)}>Updated at</div>
                      <div className={clsx(styles.colTriggerEvents, styles.subHeaderCell)}>Triggering Events</div>
                      <div className={clsx(styles.colSendgrid, styles.subHeaderCell)}>Sendgrid ID</div>
                      <div className={clsx(styles.colStatus, styles.subHeaderCell)} />
                      <div className={styles.colSpacer16} />
                    </div>
                    {filteredUncategorized.map((t) => renderTemplateRow(t, null))}
                  </div>
                </Collapse>
              </React.Fragment>
            )}

            {/* Empty state */}
            {isEmpty && (
              <div className={styles.row}>
                <div className={styles.emptyMessage}>No email templates found</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

CategorizedCommunicationTemplates.propTypes = {
  level: PropTypes.string.isRequired,
  group_id: PropTypes.string,
  location_id: PropTypes.string,
  isSync: PropTypes.bool,
  title: PropTypes.string,
};

export default CategorizedCommunicationTemplates;
